import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import { TrendingUp, LogOut, Search, Settings, ToggleLeft, ToggleRight, ArrowLeft, Network, Plus, X, Trash2, Edit2 } from 'lucide-react';
import { useAuthStore } from '../store/authStore';

import AdminNavbar from '../components/AdminNavbar';

const AdminTokensPage = () => {
    const { logout } = useAuthStore();
    const [tokens, setTokens] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Network Management State
    const [showNetworkModal, setShowNetworkModal] = useState(false);
    const [selectedToken, setSelectedToken] = useState(null);
    const [allNetworks, setAllNetworks] = useState([]);
    const [tokenNetworks, setTokenNetworks] = useState([]);
    const [loadingNetworks, setLoadingNetworks] = useState(false);
    const [newNetworkId, setNewNetworkId] = useState('');

    useEffect(() => {
        fetchTokens();
        fetchNetworks();
    }, []);

    const fetchTokens = async () => {
        try {
            const res = await api.get('/admin/tokens');
            setTokens(res.data.tokens);
        } catch (error) {
            toast.error('Failed to fetch tokens');
        } finally {
            setLoading(false);
        }
    };

    const fetchNetworks = async () => {
        try {
            const res = await api.get('/admin/networks');
            setAllNetworks(res.data.networks);
        } catch (error) {
            console.error('Failed to fetch networks');
        }
    };

    const handleToggleDeposit = async (token) => {
        const newStatus = !token.depositEnabled;
        try {
            setTokens(tokens.map(t =>
                t.id === token.id ? { ...t, depositEnabled: newStatus } : t
            ));

            await api.post(`/admin/tokens/${token.id}/toggle-deposit`, {
                enabled: newStatus
            });
            toast.success(`Deposits ${newStatus ? 'enabled' : 'disabled'} for ${token.symbol}`);
        } catch (error) {
            setTokens(tokens.map(t =>
                t.id === token.id ? { ...t, depositEnabled: !newStatus } : t
            ));
            toast.error('Failed to update status');
        }
    };

    // --- Network Management Handlers ---

    const openNetworkModal = async (token) => {
        setSelectedToken(token);
        setShowNetworkModal(true);
        setLoadingNetworks(true);
        setTokenNetworks([]);
        try {
            const res = await api.get(`/admin/tokens/${token.id}/networks`);
            setTokenNetworks(res.data.tokenNetworks);
        } catch (error) {
            toast.error('Failed to load token networks');
        } finally {
            setLoadingNetworks(false);
        }
    };

    const handleAddNetwork = async () => {
        if (!newNetworkId) return;
        try {
            const res = await api.post(`/admin/tokens/${selectedToken.id}/networks`, {
                networkId: newNetworkId
            });
            setTokenNetworks([...tokenNetworks, res.data.tokenNetwork]);
            setNewNetworkId('');
            toast.success('Network added successfully');
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to add network');
        }
    };

    const handleRemoveNetwork = async (networkId) => {
        if (!window.confirm('Are you sure? Users will lose access to deposit addresses on this network.')) return;
        try {
            await api.delete(`/admin/tokens/${selectedToken.id}/networks/${networkId}`);
            setTokenNetworks(tokenNetworks.filter(tn => tn.networkId !== networkId));
            toast.success('Network removed');
        } catch (error) {
            toast.error('Failed to remove network');
        }
    };

    const handleToggleNetworkStatus = async (tokenNetwork, field) => {
        const newValue = !tokenNetwork[field];
        try {
            // Optimistic update
            setTokenNetworks(tokenNetworks.map(tn =>
                tn.id === tokenNetwork.id ? { ...tn, [field]: newValue } : tn
            ));

            await api.put(`/admin/tokens/${selectedToken.id}/networks/${tokenNetwork.networkId}`, {
                [field]: newValue
            });
        } catch (error) {
            // Revert
            setTokenNetworks(tokenNetworks.map(tn =>
                tn.id === tokenNetwork.id ? { ...tn, [field]: !newValue } : tn
            ));
            toast.error('Failed to update network status');
        }
    };

    const filteredTokens = tokens.filter(token =>
        token.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
        token.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="min-h-screen bg-slate-900">
            <AdminNavbar />

            <div className="container mx-auto px-6 py-8">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                    <div>
                        <Link to="/admin" className="text-gray-500 hover:text-white flex items-center gap-2 mb-2">
                            <ArrowLeft size={16} /> Back to Dashboard
                        </Link>
                        <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                            <Settings className="text-blue-500" />
                            Token Management
                        </h1>
                    </div>

                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={20} />
                        <input
                            type="text"
                            placeholder="Search tokens..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="bg-slate-800 border border-slate-700 text-white pl-10 pr-4 py-2.5 rounded-xl focus:outline-none focus:border-blue-500 w-full md:w-64"
                        />
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-20 text-gray-400">Loading tokens...</div>
                ) : (
                    <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden shadow-xl">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead className="bg-slate-700/50">
                                    <tr className="text-gray-400 border-b border-slate-700">
                                        <th className="px-6 py-4 font-semibold text-sm uppercase">Asset</th>
                                        <th className="px-6 py-4 font-semibold text-sm uppercase">Global Status</th>
                                        <th className="px-6 py-4 font-semibold text-sm uppercase text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-700">
                                    {filteredTokens.map((token) => (
                                        <tr key={token.id} className="hover:bg-slate-700/30 transition-colors">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-xs font-bold text-white">
                                                        {token.symbol[0]}
                                                    </div>
                                                    <div>
                                                        <div className="font-bold text-white">{token.symbol}</div>
                                                        <div className="text-xs text-gray-500">{token.name}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <button
                                                    onClick={() => handleToggleDeposit(token)}
                                                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all border ${token.depositEnabled
                                                        ? 'bg-green-500/10 text-green-400 border-green-500/20 hover:bg-green-500/20'
                                                        : 'bg-red-500/10 text-red-400 border-red-500/20 hover:bg-red-500/20'
                                                        }`}
                                                >
                                                    {token.depositEnabled ? (
                                                        <>
                                                            <ToggleRight size={18} />
                                                            <span className="text-xs font-bold">Enabled</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <ToggleLeft size={18} />
                                                            <span className="text-xs font-bold">Disabled</span>
                                                        </>
                                                    )}
                                                </button>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <button
                                                    onClick={() => openNetworkModal(token)}
                                                    className="flex items-center gap-2 ml-auto bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium text-sm transition-colors"
                                                >
                                                    <Network size={16} />
                                                    Manage Networks
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            {/* Network Management Modal */}
            {showNetworkModal && selectedToken && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-slate-800 rounded-2xl border border-slate-700 w-full max-w-2xl shadow-2xl overflow-hidden">
                        <div className="p-6 border-b border-slate-700 flex justify-between items-center bg-slate-750">
                            <h2 className="text-xl font-bold text-white flex items-center gap-2">
                                <Network className="text-blue-500" />
                                Manage Networks: <span className="text-blue-400">{selectedToken.symbol}</span>
                            </h2>
                            <button
                                onClick={() => setShowNetworkModal(false)}
                                className="text-gray-400 hover:text-white p-1 hover:bg-slate-700 rounded-full transition-colors"
                            >
                                <X size={24} />
                            </button>
                        </div>

                        <div className="p-6">
                            {/* Add Network Section */}
                            <div className="flex gap-4 mb-8 p-4 bg-slate-700/30 rounded-xl border border-slate-700/50">
                                <select
                                    value={newNetworkId}
                                    onChange={(e) => setNewNetworkId(e.target.value)}
                                    className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
                                >
                                    <option value="">Select network to add...</option>
                                    {allNetworks
                                        .filter(n => !tokenNetworks.find(tn => tn.networkId === n.id))
                                        .map(net => (
                                            <option key={net.id} value={net.id}>
                                                {net.name} ({net.symbol})
                                            </option>
                                        ))
                                    }
                                </select>
                                <button
                                    onClick={handleAddNetwork}
                                    disabled={!newNetworkId}
                                    className="bg-blue-600 hover:bg-blue-500 text-white px-6 py-2 rounded-lg font-bold flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    <Plus size={18} /> Add
                                </button>
                            </div>

                            {/* Network List */}
                            {loadingNetworks ? (
                                <div className="text-center py-8 text-gray-500">Loading networks...</div>
                            ) : tokenNetworks.length > 0 ? (
                                <div className="space-y-3">
                                    {tokenNetworks.map(tn => (
                                        <div key={tn.id} className="bg-slate-700/20 border border-slate-700 rounded-xl p-4 flex items-center justify-between group hover:border-slate-600 transition-colors">
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center font-bold text-white border border-slate-700">
                                                    {tn.network.symbol}
                                                </div>
                                                <div>
                                                    <div className="font-bold text-white">{tn.network.name}</div>
                                                    <div className="text-xs text-gray-500 font-mono">
                                                        Min Deposit: {parseFloat(tn.minDeposit) || '0'}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-6">
                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <span className="text-xs text-gray-400 font-medium">Deposit</span>
                                                    <div
                                                        className={`w-10 h-5 rounded-full relative transition-colors ${tn.depositEnabled ? 'bg-green-500' : 'bg-slate-600'}`}
                                                        onClick={() => handleToggleNetworkStatus(tn, 'depositEnabled')}
                                                    >
                                                        <div className={`w-3 h-3 bg-white rounded-full absolute top-1 transition-transform ${tn.depositEnabled ? 'left-6' : 'left-1'}`} />
                                                    </div>
                                                </label>

                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <span className="text-xs text-gray-400 font-medium">Withdraw</span>
                                                    <div
                                                        className={`w-10 h-5 rounded-full relative transition-colors ${tn.withdrawalEnabled ? 'bg-blue-500' : 'bg-slate-600'}`}
                                                        onClick={() => handleToggleNetworkStatus(tn, 'withdrawalEnabled')}
                                                    >
                                                        <div className={`w-3 h-3 bg-white rounded-full absolute top-1 transition-transform ${tn.withdrawalEnabled ? 'left-6' : 'left-1'}`} />
                                                    </div>
                                                </label>

                                                <button
                                                    onClick={() => handleRemoveNetwork(tn.networkId)}
                                                    className="text-gray-500 hover:text-red-400 p-2 hover:bg-red-500/10 rounded-lg transition-colors"
                                                    title="Remove Network"
                                                >
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-12 text-gray-500 border-2 border-dashed border-slate-700 rounded-xl">
                                    No networks configured for {selectedToken.symbol} yet.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminTokensPage;
